/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { jsx, css } from '@emotion/react';
import { queryCache, useQuery } from 'react-query';
import PageHeader from '@atlaskit/page-header';
import { FormattedMessage, FormattedNumber, useIntl } from 'components/i18n';
import Table, { Column, AvatarCell } from 'components/table';
import Button from '@atlaskit/button';
import { Parser } from 'json2csv';
import FileSaver from 'file-saver';
import api from '../../libs/api';
import { PageWrapper } from '../../components/page';
import useSearchable from '../../hooks/useSearchable';
import TextField from '../../components/textfield';
import { CompanyAvatar } from '../../components/avatar';
import { exportToExcel } from '../../utils';

type Props = {};

type Company = {
  name: string;
  customerNumber: number;
  revenue: number;
  expenses: number;
  profit: number;
  inactiveBankConnections: number;
  isProtectedCompany: boolean;
  subscriptionActive: boolean;
};

let defaultYear = new Date().getFullYear();
if (new Date().getMonth() < 3) {
  defaultYear-=1;
}

const years = [defaultYear+1, defaultYear, defaultYear-1, defaultYear-2, defaultYear-3, defaultYear-4, defaultYear-5, defaultYear-6, defaultYear-7];

const exportCompaniesToExcel = (data: Company[], year: number) => {
  const mapping = {
    customerNumber: 'Klantnummer',
    name: 'Bedrijfsnaam',
    revenue: 'Omzet',
    expense: 'Kosten',
    profit: 'Winst',
    inactiveBankConnections: 'Bank inactief',
    subscriptionActive: 'Abonnement actief',
    endDate: 'Einddatum abonnement',
  };
  exportToExcel(data, `bedrijven-financiën-${year}`, mapping);
};
export const CompanyCell = React.memo(function CompanyCell({company}: {company?: Company}) {
  const avatar = React.useMemo(()=><CompanyAvatar size="medium" company={company!} />, [company?.name]);
  return (
    <AvatarCell
      avatar={avatar}
      primaryText={company!.name}
    />
  );
});
const CompanyFiancialsPage: React.FC<Props> = (props) => {

  const intl = useIntl();
  const [year, setYear ] = useState(defaultYear);
  const [showProtected, setShowProtected] = useState(false);
  const [showActive, setShowActive] = useState(true);

  const { data, error, isLoading } = useQuery(
    ['financial-overview', year],
    () => api.get('/Companies/financial-overview', {params: {year}}),
  );

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((company: Company) => {
      return company.isProtectedCompany === showProtected && company.subscriptionActive === showActive;
    });
  }, [data, showProtected, showActive]);

  const [search, setSearch] = useState('');
  const companies: Company[] = useSearchable(filteredData, search, {
    keys: ['name', 'customerNumber']
  }) || [];

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value), []);

  const columns: Column<Company>[] = useMemo(() => [
    {
      Header: 'Klantnummer',
      accessor: 'customerNumber',
      width: 60,
    },
    {
      Header: <FormattedMessage id="admin.GeneralLedgerPage.headings.name" defaultMessage="Name" />,
      accessor: 'name',
      width: 240,
      Cell: ({row: {original}}) => <CompanyCell company={original} />,
    },
    {
      Header: 'Omzet',
      accessor: 'revenue',
      width: 100,
      Cell: ({value}) => <FormattedNumber value={value as number} />
    },
    {
      Header: 'Kosten',
      accessor: 'expense',
      width: 100,
      Cell: ({value}) => <FormattedNumber value={value as number} />
    },
    {
      Header: 'Winst',
      accessor: 'profit',
      width: 100,
      Cell: ({value}) => <FormattedNumber value={value as number} />
    },
    {
      Header: 'Bank inactief',
      accessor: 'inactiveBankConnections',
      width: 100,
      Cell: ({value}) => value!== 0 ? value : null
    },
    {
      Header: 'Einddatum abonnement',
      accessor: 'endDate',
      width: 100,
      Cell: ({value}) => value ? new Date(value).toLocaleDateString() : null
    },
  ], []);

  const actionsContent = React.useMemo(() => {
    const searchLabel = intl.formatMessage({
      id: 'admin.GeneralLedgerPage.search',
      defaultMessage: 'Search',
      description: 'Search placeholder',
    });

    const itemsContainer = css`
      display: flex;
      align-items: center;
      justify-content: flex-end;
    `;

    return (
      <div css={itemsContainer}>
        <div
          className=""
          css={css`
              margin-right: 1rem`}
        >
          <input
            id="checkbox1"
            type="checkbox"
            checked={showActive}
            onChange={() => setShowActive(!showActive)}
          />
          <label css={css`margin-left: 0.5rem`} htmlFor="checkbox1">Abonnement actief</label>
        </div>
        <div
          className=""
          css={css`
              margin-right: 1rem`}
        >
          <input
            id="checkbox2"
            type="checkbox"
            checked={showProtected}
            onChange={() => setShowProtected(!showProtected)}
          />
          <label css={css`margin-left: 0.5rem`} htmlFor="checkbox2">Afgeschermd</label>
        </div>
        <div css={css`flex: 0 0 65px;
            margin-right: 1rem`}
        >
          <select
            css={css`padding: 0.3rem;
                border-radius: 0.25rem;`}
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
          >
            {years.map(year => <option key={year} value={year}>{year}</option>)}
          </select>
        </div>
        <div css={css`flex: 0 0 300px;
            margin-right: 1rem`}
        >
          <TextField
            placeholder={searchLabel}
            isCompact
            aria-label={searchLabel} // onChange={handleSearch}
            onChange={handleSearch}
          />
        </div>
        <Button
          appearance="primary"
          onClick={() => {
            exportCompaniesToExcel(companies, year);
          }}
        >
          Export
        </Button>
      </div>
    );
  }
  , [intl, year, companies, showActive, showProtected]);

  return (
    <PageWrapper fullWidth>
      <PageHeader actions={actionsContent}>
        Bedrijven financiën
      </PageHeader>
      <Table<Company> columns={columns} data={companies || []} isLoading={isLoading} sortBy="profit DESC" />
    </PageWrapper>
  );

};

export default CompanyFiancialsPage;
